import type { OperationItem } from '../types'
import type { OperationPresenter } from './registry'
import {
  allergenDetectContextSpec,
  allergenDetectOutputSchema,
  menuDetectMetadataContextSpec,
  menuDetectMetadataOutputSchema,
  menuTranslateContextSpec,
  menuTranslateOutputSchema,
  type OperationContextSpec,
  productsDetectMetadataContextSpec,
  productsDetectMetadataOutputSchema,
  textGenerateContextSpec,
  translateProductsOutputSchema,
} from '@foodlista/operations'

/**
 * Per-type BOC summaries. Pure and free of Nuxt/Vue imports so they can be
 * exercised directly — the catalog modules only wire them into the registry.
 *
 * Honesty rules these encode: a run that completed with a fallback or a
 * skipped write must never read as a success, and a count may only be claimed
 * for what the output confirms. Where the output confirms nothing per entity,
 * the subtitle says "analyzed" rather than "enriched". An unreadable context or
 * output yields `undefined`, which makes the row fall back to its status line.
 */

/**
 * Reads an envelope context only when it was written by the projection version
 * this code understands. Without the version check a v2 envelope that reused a
 * key with new semantics would be parsed as if it were v1.
 */
function readContext<TContext>(
  item: OperationItem,
  spec: OperationContextSpec<never, TContext>,
): TContext | undefined {
  if (item.contextVersion !== spec.version) {
    return undefined
  }

  const parsed = spec.schema.safeParse(item.context)
  return parsed.success ? parsed.data : undefined
}

function productsLink(restaurantSlug: string) {
  return {
    path: `/dashboard/restaurants/${restaurantSlug}/products`,
    labelKey: 'operations.boc.links.viewProducts',
  }
}

/**
 * The menu page is addressed by slug, so a run enqueued without one (the
 * product-catalog flow, or a caller predating the field) simply has no target.
 */
function menuLink(restaurantSlug: string, menuSlug: string | undefined) {
  return menuSlug
    ? {
        path: `/dashboard/restaurants/${restaurantSlug}/menus/${menuSlug}`,
        labelKey: 'operations.boc.links.viewMenu',
      }
    : undefined
}

export const presentAllergenDetect: OperationPresenter = ({ item, run, t }) => {
  const context = readContext(item, allergenDetectContextSpec)
  const output = run
    ? allergenDetectOutputSchema.safeParse(run.output)
    : undefined

  const count = output?.success ? output.data.allergens.length : undefined
  const saved = output?.success && output.data.persistence.status === 'applied'
  const subtitle = count === undefined
    ? undefined
    : t(
      `operations.boc.subtitles.allergenDetect.${saved ? 'saved' : 'detected'}`,
      { count },
      count,
    )

  const link = context?.productId
    ? {
        path: `/dashboard/restaurants/${item.restaurantSlug}/products/${context.productId}`,
        labelKey: 'operations.boc.links.viewProduct',
      }
    : undefined

  if (!subtitle && !link) {
    return undefined
  }

  return { subtitle, link }
}

// The generated text lands in the field that asked for it, and no input
// identifies that field as a route — so the row links back to the page the
// request came from, when the caller had one (drafts do not).
export const presentTextGenerate: OperationPresenter = ({ item, t }) => {
  const context = readContext(item, textGenerateContextSpec)

  return {
    subtitle: t('operations.boc.subtitles.textGenerate.done'),
    link: context?.originPath
      ? {
          path: context.originPath,
          labelKey: 'operations.boc.links.viewChanges',
        }
      : undefined,
  }
}

export const presentMenuTranslate: OperationPresenter = ({ item, run, t }) => {
  const context = readContext(item, menuTranslateContextSpec)
  const output = run
    ? menuTranslateOutputSchema.safeParse(run.output)
    : undefined
  if (!output?.success) {
    return undefined
  }

  return {
    subtitle: output.data.success
      ? t('operations.boc.subtitles.menuTranslate.done')
      : t('operations.boc.subtitles.menuTranslate.fallback'),
    link: menuLink(item.restaurantSlug, context?.menuSlug),
  }
}

export const presentMenuDetectMetadata: OperationPresenter = ({
  item,
  run,
  t,
}) => {
  const context = readContext(item, menuDetectMetadataContextSpec)
  const output = run
    ? menuDetectMetadataOutputSchema.safeParse(run.output)
    : undefined
  if (!context || !output?.success) {
    return undefined
  }

  const link = menuLink(item.restaurantSlug, context.menuSlug)

  if (!output.data.success) {
    return {
      subtitle: t('operations.boc.subtitles.menuDetectMetadata.fallback'),
      link,
    }
  }

  const count = context.productCount
  return {
    subtitle: t(
      'operations.boc.subtitles.menuDetectMetadata.analyzed',
      { count },
      count,
    ),
    link,
  }
}

export const presentProductsDetectMetadata: OperationPresenter = ({
  item,
  run,
  t,
}) => {
  const context = readContext(item, productsDetectMetadataContextSpec)
  const output = run
    ? productsDetectMetadataOutputSchema.safeParse(run.output)
    : undefined
  if (!context || !output?.success) {
    return undefined
  }

  const link = productsLink(item.restaurantSlug)

  if (!output.data.success) {
    return {
      subtitle: t('operations.boc.subtitles.productsDetectMetadata.fallback'),
      link,
    }
  }

  const count = context.productCount
  return {
    subtitle: t(
      'operations.boc.subtitles.productsDetectMetadata.analyzed',
      { count },
      count,
    ),
    link,
  }
}

export const presentTranslateProducts: OperationPresenter = ({
  item,
  run,
  t,
}) => {
  const output = run
    ? translateProductsOutputSchema.safeParse(run.output)
    : undefined
  if (!output?.success) {
    return undefined
  }

  const count = output.data.results.filter(
    result => result.status === 'applied',
  ).length

  return {
    subtitle: t(
      'operations.boc.subtitles.translateProducts.done',
      { count },
      count,
    ),
    link: productsLink(item.restaurantSlug),
  }
}
